import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Divider, Skeleton, Typography } from "@mui/material";

import { getServicePageRoute } from "../../../../../../routers/routeUtils";
import { formatLocalizedTimeAgo } from "../../../../../../utils/formatDate";

import { useUserDataFormatter } from "../../../../../User/hooks/useUserDataFormatter";

import { ServiceProgram } from "../../../../types/service.type";
import { ServiceTypeEnum } from "../../../../enum/service.enum";

export const ProgramCard = ({ program }: { program: ServiceProgram }) => {
  const {
    createdAt,
    program: { name, id, user: ProgramUser },
    user,
  } = program;

  const { i18n } = useTranslation();
  const programOwner = ProgramUser || user;

  const { firstTwoChr, fullName, profileLink, media, role } = useUserDataFormatter(programOwner);

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.serviceName} component={Link} to={getServicePageRoute(id, ServiceTypeEnum.Program)}>
        {name}
      </Typography>

      <Divider sx={styles.divider} />
      <Box sx={styles.serviceProviderContainer}>
        <Avatar sx={styles.serviceProviderAvatar} src={media}>
          <Typography>{firstTwoChr}</Typography>
        </Avatar>
        <Box>
          <Typography component={Link} to={profileLink} sx={styles.serviceProviderName}>
            {fullName}
          </Typography>
          <Typography sx={styles.serviceProviderRole}>{role}</Typography>
        </Box>
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.serviceDetailsContainer}>
        <Typography sx={styles.serviceDetailsTime}>{formatLocalizedTimeAgo(createdAt, i18n.language)}</Typography>
      </Box>
    </Box>
  );
};

export const ProgramCardLoader = () => {
  return (
    <Box sx={styles.container}>
      <Skeleton variant="text" width="60%" height={24} sx={styles.serviceName} />

      <Divider sx={styles.divider} />
      <Box sx={styles.serviceProviderContainer}>
        <Skeleton variant="circular" sx={styles.serviceProviderAvatar} />
        <Box>
          <Skeleton variant="text" width={120} height={20} sx={styles.serviceProviderName} />
          <Skeleton variant="text" width={80} height={16} sx={styles.serviceProviderRole} />
        </Box>
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.serviceDetailsContainer}>
        <Skeleton variant="text" width={100} height={16} sx={styles.serviceDetailsTime} />
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #E7E7E7",
  },
  serviceNameContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  serviceName: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Inter",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    textDecoration: "none",
  },
  statusChip: (status: string) => ({
    padding: "2px 8px",
    borderRadius: "16px",
    border: "1px solid",
    background: "#FEF0E6",
    fontFamily: "Inter",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    height: "fit-content",
    ...styles.statusStyles(status),
    "& .MuiChip-label": {
      padding: 0,
    },
  }),
  statusStyles: (status: string) => {
    if (status === "rejected") {
      return {
        color: "#EF4444",
        backgroundColor: "#FDE8E8",
        borderColor: "#F9B9B9",
      };
    }
    if (status === "initiated") {
      return {
        color: "var(--Blue-700, #175CD3)",
        backgroundColor: "var(--Blue-50, #EFF8FF)",
        borderColor: "var(--Blue-200, #B2DDFF)",
      };
    }
    if (status === "negotiated") {
      return {
        color: "#F97316",
        backgroundColor: "#FEF0E6",
        borderColor: "#FCD3B5",
      };
    }
    if (status === "contracted") {
      return {
        color: "var(--Blue-700, #175CD3)",
        backgroundColor: "#FEF0E6",
        borderColor: "#FCD3B5",
      };
    }
    if (status === "completed") {
      return {
        color: "#076D0D",
        backgroundColor: "#E7FDE8",
        borderColor: "#7EF785",
      };
    }
    if (status === "canceled") {
      return {
        color: "#344054",
        backgroundColor: "#EFF1F5",
        borderColor: "#D0D6E2",
      };
    }
    return {};
  },
  divider: {
    mx: "-16px",
  },
  serviceProviderContainer: {
    display: "flex",
    gap: "12px",
  },
  serviceProviderAvatar: {
    width: "40px",
    height: "40px",
  },
  serviceProviderName: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    textDecoration: "none",
  },
  serviceProviderRole: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  serviceDetailsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  serviceDetailsTime: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
};
